import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Save, Plus, Trash2, Lock } from 'lucide-react';
import { api, getToken } from '../lib/api';
import { useToast } from '../components/Toast';
import { getScopedDomain } from '../lib/tenantScope';
import DidPicker from '../components/DidPicker';

const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });
const fJson = async (url: string, init: RequestInit = {}) => {
  const r = await fetch(url, { ...init, headers: { ...auth().headers, ...(init.headers || {}) } });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
};

// Stratus stores 3 distinct backend strategies plus FreeSWITCH dial-string
// variations for the rest. We translate on save.
const STRATEGY_OPTIONS = [
  { v: 'priority_hunt', label: 'Prioritized Hunt' },
  { v: 'round_robin',   label: 'Round Robin' },
  { v: 'hunt',          label: 'Hunt' },
  { v: 'ring_all',      label: 'Ring All' },
  { v: 'random',        label: 'Random' },
  { v: 'linear',        label: 'Linear' },
  { v: 'paging',        label: 'Paging (multicast)' },
];

const DESTINATION_PRESETS = [
  { v: 'end_call',         label: 'End Call' },
  { v: 'voicemail',        label: 'Voicemail' },
  { v: 'extension',        label: 'Extension' },
  { v: 'ring_group',       label: 'Ring Group' },
  { v: 'queue',            label: 'Queue' },
  { v: 'ivr',              label: 'IVR' },
  { v: 'external',         label: 'External Number' },
  { v: 'accept_anyway',    label: 'Accept Anyway (ignore office hours)' },
];

export default function RingGroupEditor() {
  const { ring_group_uuid: routeId } = useParams<{ ring_group_uuid: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const [tab, setTab] = useState<'general' | 'users' | 'notifications' | 'options' | 'talk'>('general');

  // /ring-groups/new doesn't supply :ring_group_uuid — detect by path.
  const isNew = !routeId && /\/ring-groups\/new\/?$/.test(location.pathname);
  const ring_group_uuid = routeId || (isNew ? 'new' : '');

  const rg = useQuery<any>({
    queryKey: ['ring-group-settings', ring_group_uuid],
    queryFn: () => fJson(`/api/call-routing/ring-groups/${ring_group_uuid}/settings`),
    enabled: !!ring_group_uuid && !isNew,
  });

  // In create-mode, non-General tabs need a saved RG before they can load.
  const lockedTab = (t: string) => isNew && t !== 'general';
  function pickTab(t: any) {
    if (lockedTab(t)) {
      toast('error', 'Save the ring group first — the other tabs need an extension to attach to.');
      return;
    }
    setTab(t);
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/ring-groups')} className="btn-ghost inline-flex items-center gap-1"><ArrowLeft size={14} /> Call Handling</button>
        <h1 className="text-xl font-semibold flex-1">
          {isNew ? 'Add Ring Group' : `Edit Ring Group${rg.data ? ` — ${rg.data.ring_group_extension || ''} (${rg.data.ring_group_name || ''})` : ''}`}
        </h1>
      </div>

      <div className="flex gap-1 border-b border-border overflow-x-auto">
        {[
          ['general', 'General'],
          ['users', 'Users'],
          ['notifications', 'Notifications'],
          ['options', 'Options'],
          ['talk', 'Stratus Talk'],
        ].map(([v, l]) => {
          const locked = lockedTab(v as string);
          return (
            <button
              key={v}
              onClick={() => pickTab(v)}
              title={locked ? 'Save the ring group first to unlock this tab' : undefined}
              className={`px-4 py-2 text-sm whitespace-nowrap inline-flex items-center gap-1 ${
                tab === v ? 'border-b-2 border-primary text-primary'
                : locked ? 'text-textSecondary/40 cursor-not-allowed'
                : 'text-textSecondary hover:text-textPrimary'
              }`}
            >
              {l}
              {locked && <Lock size={10} />}
            </button>
          );
        })}
      </div>

      {tab === 'general' && (isNew
        ? <NewRingGroupGeneralPanel toast={toast} navigate={navigate} qc={qc} />
        : (rg.data && <GeneralTab rg={rg.data} ring_group_uuid={ring_group_uuid!} qc={qc} toast={toast} />))}
      {!isNew && tab === 'users'         && <UsersTab ring_group_uuid={ring_group_uuid!} qc={qc} toast={toast} />}
      {!isNew && tab === 'notifications' && <NotificationsTab ring_group_uuid={ring_group_uuid!} qc={qc} toast={toast} />}
      {!isNew && tab === 'options'       && <OptionsTab ring_group_uuid={ring_group_uuid!} qc={qc} toast={toast} />}
      {!isNew && tab === 'talk'          && <TalkTab ring_group_uuid={ring_group_uuid!} />}
    </div>
  );
}

// Add Ring Group → General tab in create mode. Same visual layout as the
// edit-mode GeneralTab but POSTs to /api/tenants/<id>/ring-groups on save and
// redirects to the editor.
function NewRingGroupGeneralPanel({ toast, navigate, qc }: any) {
  const me = api.me();
  const tenants = useQuery<any[]>({ queryKey: ['tenants'], queryFn: () => fJson('/api/tenants') });

  const [form, setForm] = useState<any>({
    ring_group_extension: '',
    ring_group_name: '',
    strategy: 'priority_hunt',
    domain_uuid: me?.domain_uuid || '',
    ring_group_timeout: 20,
    assigned_dids: [],
    destination_if_no_answer: { type: 'end_call' },
    office_closed_route: { type: 'accept_anyway' },
    on_break_route: { type: 'accept_anyway' },
    on_holiday_route: { type: 'accept_anyway' },
    play_announcement_office_closed: false,
    play_announcement_break: false,
    play_announcement_holiday: false,
  });
  const set = (k: string, v: any) => setForm({ ...form, [k]: v });

  // Prefill the next free virtual extension — 3CX auto-assigns this, so the
  // field is never empty (an empty extension is what greys out Save).
  const nextExt = useQuery<any>({ queryKey: ['ch-next-extension'], queryFn: () => fJson('/api/call-handling/next-extension') });
  useEffect(() => {
    const n = nextExt.data && nextExt.data.next;
    if (n) setForm((f: any) => (f.ring_group_extension ? f : { ...f, ring_group_extension: String(n) }));
  }, [nextExt.data]);

  const create = useMutation({
    mutationFn: () => fJson(`/api/tenants/${form.domain_uuid}/ring-groups`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ring_group_name: form.ring_group_name,
        ring_group_extension: form.ring_group_extension,
        ring_group_strategy: form.strategy,
        ring_group_timeout: form.ring_group_timeout,
        assigned_dids: form.assigned_dids,
        members: [],
      }),
    }),
    onSuccess: (r: any) => {
      qc.invalidateQueries({ queryKey: ['ring-groups', form.domain_uuid] });
      toast('success', `Ring Group ${form.ring_group_extension} created`);
      navigate(`/ring-groups/${r.ring_group_uuid}/edit`);
    },
    onError: (e: any) => toast('error', e.message),
  });

  return (
    <div className="card p-4 space-y-5">
      {/* Top row mirrors 3CX 140821 layout */}
      <div className="grid grid-cols-2 gap-4">
        <Field label="Virtual Extension Number *">
          <input className="input" value={form.ring_group_extension} onChange={e => set('ring_group_extension', e.target.value)} placeholder="e.g. 8001" />
        </Field>
        <Field label="Assigned DID number(s)">
          <DidPicker value={form.assigned_dids} onChange={v => set('assigned_dids', v)} selfRef={form.ring_group_extension} />
        </Field>
        <Field label="Ring Group Name *">
          <input className="input" value={form.ring_group_name} onChange={e => set('ring_group_name', e.target.value)} />
        </Field>
        <Field label="Ring Strategy *">
          <select className="input" value={form.strategy} onChange={e => set('strategy', e.target.value)}>
            {STRATEGY_OPTIONS.map(s => <option key={s.v} value={s.v}>{s.label}</option>)}
          </select>
        </Field>
        <Field label="Department *">
          <select className="input" value={form.domain_uuid} onChange={e => set('domain_uuid', e.target.value)}>
            <option value="">— Choose —</option>
            {(tenants.data || []).map(t => <option key={t.domain_uuid} value={t.domain_uuid}>{t.domain_description || t.domain_name}</option>)}
          </select>
        </Field>
        <Field label="Ring Time (seconds) *">
          <input className="input" type="number" min={5} max={300} value={form.ring_group_timeout}
                 onChange={e => set('ring_group_timeout', parseInt(e.target.value, 10) || 20)} />
        </Field>
      </div>

      <h2 className="font-semibold pt-2">Destinations</h2>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Destination if no answer">
          <select className="input" value={form.destination_if_no_answer?.type || 'end_call'}
                  onChange={e => set('destination_if_no_answer', { type: e.target.value })}>
            {DESTINATION_PRESETS.map(d => <option key={d.v} value={d.v}>{d.label}</option>)}
          </select>
        </Field>
        <DestWithToggle label="When office is closed route to" route={form.office_closed_route}
          announce={form.play_announcement_office_closed}
          onRoute={v => set('office_closed_route', v)} onAnnounce={v => set('play_announcement_office_closed', v)} />
        <DestWithToggle label="When on break route to" route={form.on_break_route}
          announce={form.play_announcement_break}
          onRoute={v => set('on_break_route', v)} onAnnounce={v => set('play_announcement_break', v)} />
        <DestWithToggle label="When on holiday route to" route={form.on_holiday_route}
          announce={form.play_announcement_holiday}
          onRoute={v => set('on_holiday_route', v)} onAnnounce={v => set('play_announcement_holiday', v)} />
      </div>

      <div className="flex justify-end pt-2 border-t border-border">
        <button
          onClick={() => create.mutate()}
          disabled={!form.ring_group_extension || !form.ring_group_name || !form.domain_uuid || create.isPending}
          title={
            !form.ring_group_extension ? 'Enter a virtual extension number'
            : !form.ring_group_name ? 'Enter a ring group name'
            : !form.domain_uuid ? 'Choose a department'
            : undefined
          }
          className="btn-primary inline-flex items-center gap-1 disabled:opacity-50"
        >
          <Save size={14} /> {create.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

function DestWithToggle({ label, route, announce, onRoute, onAnnounce }: any) {
  return (
    <div>
      <Field label={label}>
        <select className="input" value={route?.type || 'accept_anyway'} onChange={e => onRoute({ type: e.target.value })}>
          {DESTINATION_PRESETS.map(d => <option key={d.v} value={d.v}>{d.label}</option>)}
        </select>
      </Field>
      <label className="text-sm flex items-center gap-2 mt-1">
        <input type="checkbox" checked={!!announce} onChange={e => onAnnounce(e.target.checked)} />
        Play Announcement
      </label>
    </div>
  );
}

// DidPicker (DID inventory autosuggest, 3CX-style) moved to
// components/DidPicker.tsx so the Digital Receptionist editor can share it (#82).

function GeneralTab({ rg, ring_group_uuid, qc, toast }: any) {
  const [form, setForm] = useState<any>(rg);
  useEffect(() => setForm(rg), [rg.ring_group_uuid]);
  const set = (k: string, v: any) => setForm({ ...form, [k]: v });
  const setRoute = (k: string, v: any) => set(k, v);

  const tenants = useQuery<any[]>({ queryKey: ['tenants'], queryFn: () => fJson('/api/tenants') });
  // Mailbox options for a "Voicemail" destination — the tenant's users (#134).
  const mailboxes = useQuery<any[]>({
    queryKey: ['rg-mailboxes', form.domain_uuid],
    queryFn: () => fJson('/api/users?domain_uuid=' + encodeURIComponent(form.domain_uuid)),
    enabled: !!form.domain_uuid,
  });
  // Target lists so destinations are picked from a dropdown, not typed (#266).
  const _dom = form.domain_uuid ? '?domain_uuid=' + encodeURIComponent(form.domain_uuid) : '';
  const ringGroupsQ = useQuery<any[]>({ queryKey: ['rg-rg-targets', form.domain_uuid], queryFn: () => fJson('/api/ring-groups' + _dom), enabled: !!form.domain_uuid });
  const queuesQ     = useQuery<any[]>({ queryKey: ['rg-queue-targets'], queryFn: () => fJson('/api/queues') });
  const ivrsQ       = useQuery<any[]>({ queryKey: ['rg-ivr-targets', form.domain_uuid], queryFn: () => fJson('/api/ivr' + _dom), enabled: !!form.domain_uuid });
  const targetLists = {
    ringGroups: (ringGroupsQ.data || []),
    queues: (queuesQ.data || []).filter((x: any) => !form.domain_uuid || x.domain_uuid === form.domain_uuid),
    ivrs: (ivrsQ.data || []),
  };

  const save = useMutation({
    mutationFn: () => fJson(`/api/call-routing/ring-groups/${ring_group_uuid}/settings`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(form),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ring-group-settings', ring_group_uuid] }); toast('success', 'Saved'); },
    onError: (e: any) => toast('error', e.message),
  });

  return (
    <div className="card p-4 space-y-5">
      {/* Top row mirrors screenshot 140821 layout */}
      <div className="grid grid-cols-2 gap-4">
        <Field label="Virtual Extension Number *">
          <input className="input" value={form.ring_group_extension || ''} onChange={e => set('ring_group_extension', e.target.value)} placeholder="8130" />
        </Field>
        <Field label="Assigned DID number(s)">
          <DidPicker value={form.assigned_dids} onChange={v => set('assigned_dids', v)} selfRef={form.ring_group_extension} />
        </Field>
        <Field label="Ring Group Name *">
          <input className="input" value={form.ring_group_name || ''} onChange={e => set('ring_group_name', e.target.value)} />
        </Field>
        <Field label="Ring Strategy *">
          <select className="input" value={form.strategy || 'priority_hunt'} onChange={e => set('strategy', e.target.value)}>
            {STRATEGY_OPTIONS.map(s => <option key={s.v} value={s.v}>{s.label}</option>)}
          </select>
        </Field>
        <Field label="Department *">
          <select className="input" value={form.domain_uuid || ''} onChange={e => set('domain_uuid', e.target.value)}>
            <option value="">— Choose —</option>
            {(tenants.data || []).map(t => <option key={t.domain_uuid} value={t.domain_uuid}>{t.domain_description || t.domain_name}</option>)}
          </select>
        </Field>
        <Field label="Ring Time (seconds) *">
          <input className="input" type="number" min={5} max={300} value={form.ring_group_timeout_app || form.ring_time_seconds || 20}
            onChange={e => set('ring_time_seconds', parseInt(e.target.value) || 20)} />
        </Field>
      </div>

      {/* Paging-only fields (only show when strategy === 'paging') */}
      {form.strategy === 'paging' && (
        <div className="card p-3 bg-elevated/30 grid grid-cols-2 gap-3">
          <Field label="Multicast Address (paging)">
            <input className="input font-mono" value={form.multicast_address || ''} onChange={e => set('multicast_address', e.target.value)} placeholder="224.0.1.116" />
          </Field>
          <Field label="Multicast Port">
            <input className="input" type="number" value={form.multicast_port || 5060} onChange={e => set('multicast_port', parseInt(e.target.value) || 5060)} />
          </Field>
          <Field label="Codec">
            <select className="input" value={form.multicast_codec || 'PCMU'} onChange={e => set('multicast_codec', e.target.value)}>
              {['PCMU', 'PCMA', 'G722'].map(c => <option key={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Packet time (ms)">
            <input className="input" type="number" min={20} max={60} value={form.multicast_packet_time_ms || 20}
              onChange={e => set('multicast_packet_time_ms', parseInt(e.target.value) || 20)} />
          </Field>
        </div>
      )}

      {/* Destinations — 2x2 grid mirroring screenshot 140821 */}
      <div>
        <div className="font-medium border-b border-border pb-1 mb-3">Destinations</div>
        <div className="grid grid-cols-2 gap-x-4 gap-y-3">
          <DestinationCell
            label="Destination if no answer"
            value={form.forward_no_answer}
            onChange={(v: any) => setRoute('forward_no_answer', v)}
            mailboxes={mailboxes.data || []}
            targets={targetLists}
          />
          <DestinationCell
            label="When office is closed route to"
            value={form.out_of_office_route}
            onChange={(v: any) => setRoute('out_of_office_route', v)}
            mailboxes={mailboxes.data || []}
            targets={targetLists}
            announcementToggle={!!form.out_of_office_announcement}
            onAnnouncementToggle={(v: boolean) => set('out_of_office_announcement', v)}
          />
          <DestinationCell
            label="When on break route to"
            value={form.break_route}
            onChange={(v: any) => setRoute('break_route', v)}
            mailboxes={mailboxes.data || []}
            targets={targetLists}
            announcementToggle={!!form.break_announcement}
            onAnnouncementToggle={(v: boolean) => set('break_announcement', v)}
          />
          <DestinationCell
            label="When on holiday route to"
            value={form.holidays_route}
            onChange={(v: any) => setRoute('holidays_route', v)}
            mailboxes={mailboxes.data || []}
            targets={targetLists}
            announcementToggle={!!form.holidays_announcement}
            onAnnouncementToggle={(v: boolean) => set('holidays_announcement', v)}
          />
        </div>
      </div>

      <div className="flex justify-end">
        <button onClick={() => save.mutate()} className="btn-primary inline-flex items-center gap-1"><Save size={14} /> Save</button>
      </div>
    </div>
  );
}

function DestinationCell({ label, value, onChange, mailboxes, targets, announcementToggle, onAnnouncementToggle }: {
  label: string; value: any; onChange: (v: any) => void;
  mailboxes?: any[];
  targets?: { ringGroups: any[]; queues: any[]; ivrs: any[] };
  announcementToggle?: boolean; onAnnouncementToggle?: (v: boolean) => void;
}) {
  const v = value || {};
  const t = targets || { ringGroups: [], queues: [], ivrs: [] };
  return (
    <div>
      <label className="block text-sm text-textSecondary mb-1">{label}</label>
      <select
        className="input"
        value={v.type || 'end_call'}
        onChange={e => onChange({ ...v, type: e.target.value })}
      >
        {DESTINATION_PRESETS.map(d => <option key={d.v} value={d.v}>{d.label}</option>)}
      </select>
      {v.type === 'voicemail' && (
        <select className="input mt-2" value={v.target || ''} onChange={e => onChange({ ...v, target: e.target.value })}>
          <option value="">— This ring group's own mailbox —</option>
          {(mailboxes || []).map((u: any) => <option key={u.extension_uuid} value={u.extension}>{u.extension} — {u.name}</option>)}
        </select>
      )}
      {v.type === 'extension' && (
        <select className="input mt-2" value={v.target || ''} onChange={e => onChange({ ...v, target: e.target.value })}>
          <option value="">— choose extension —</option>
          {(mailboxes || []).map((u: any) => <option key={u.extension_uuid} value={u.extension}>{u.extension} — {u.name}</option>)}
        </select>
      )}
      {v.type === 'ring_group' && (
        <select className="input mt-2" value={v.target || ''} onChange={e => onChange({ ...v, target: e.target.value })}>
          <option value="">— choose ring group —</option>
          {t.ringGroups.map((r: any) => <option key={r.id} value={r.extension}>{r.extension} — {r.name}</option>)}
        </select>
      )}
      {v.type === 'queue' && (
        <select className="input mt-2" value={v.target || ''} onChange={e => onChange({ ...v, target: e.target.value })}>
          <option value="">— choose queue —</option>
          {t.queues.map((qq: any) => <option key={qq.id} value={qq.extension || qq.id}>{(qq.extension ? qq.extension + ' — ' : '') + qq.name}</option>)}
        </select>
      )}
      {v.type === 'ivr' && (
        <select className="input mt-2" value={v.target || ''} onChange={e => onChange({ ...v, target: e.target.value })}>
          <option value="">— choose digital receptionist —</option>
          {t.ivrs.map((i: any) => <option key={i.id} value={i.extension}>{i.extension} — {i.name}</option>)}
        </select>
      )}
      {v.type === 'external' && (
        <input className="input mt-2 font-mono" value={v.target || ''} onChange={e => onChange({ ...v, target: e.target.value })} placeholder="+1 555 123 4567" />
      )}
      {onAnnouncementToggle !== undefined && (
        <label className="text-xs flex items-center gap-2 mt-2">
          <input type="checkbox" checked={!!announcementToggle} onChange={e => onAnnouncementToggle(e.target.checked)} />
          Play Announcement
        </label>
      )}
    </div>
  );
}

function UsersTab({ ring_group_uuid, qc, toast }: any) {
  const members = useQuery<any[]>({
    queryKey: ['ring-group-members', ring_group_uuid],
    queryFn: () => fJson(`/api/ring-groups/${ring_group_uuid}/destinations`).catch(() => []),
  });
  // Scope the add-user list to the console's active department (the top-bar view
  // filter). fJson bypasses the ?domain_uuid injection that lib/api.ts does, so
  // apply it here — otherwise every department's users are offered.
  const scope = getScopedDomain();
  const allExt = useQuery<any[]>({
    queryKey: ['users-list', scope || 'all'],
    queryFn: () => fJson('/api/users' + (scope ? `?domain_uuid=${encodeURIComponent(scope)}` : '')),
  });
  const [add, setAdd] = useState('');

  const addMember = useMutation({
    mutationFn: (extension_uuid: string) => fJson(`/api/ring-groups/${ring_group_uuid}/destinations`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ extension_uuid }),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ring-group-members', ring_group_uuid] }); setAdd(''); },
    onError: (e: any) => toast('error', e.message),
  });
  const overrides = useQuery<any[]>({
    queryKey: ['ring-group-overrides', ring_group_uuid],
    queryFn: () => fJson(`/api/call-routing/ring-groups/${ring_group_uuid}/settings`).then((r: any) => r.member_overrides || []),
  });
  const removeMember = useMutation({
    mutationFn: (destination_uuid: string) => fJson(`/api/ring-groups/${ring_group_uuid}/destinations/${destination_uuid}`, { method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ring-group-members', ring_group_uuid] }); toast('success', 'Removed from ring group'); },
    onError: (e: any) => toast('error', e.message),
  });

  // A user already in the group can't be added again — filter them out of the picker so it's
  // impossible to double-add, and disable the button while an add is in flight (the dialplan rebuild
  // takes a moment; without this, operators rage-clicked and created duplicate members).
  const memberExts = new Set((members.data || []).map((m: any) => String(m.user_id || m.extension)));

  return (
    <div className="card p-4 space-y-3">
      <div className="font-medium">Users in this ring group</div>
      <div className="flex gap-2">
        <select className="input flex-1" value={add} onChange={e => setAdd(e.target.value)} disabled={addMember.isPending}>
          <option value="">— Add a user —</option>
          {(allExt.data || []).filter((u: any) => !memberExts.has(String(u.extension))).map((u: any) => <option key={u.extension_uuid} value={u.extension_uuid}>{u.extension} — {u.name}</option>)}
        </select>
        <button className="btn-primary inline-flex items-center gap-1 disabled:opacity-50" disabled={!add || addMember.isPending} onClick={() => add && addMember.mutate(add)}><Plus size={14} /> {addMember.isPending ? 'Adding…' : 'Add User'}</button>
      </div>
      <table className="table">
        <thead><tr><th>Extension</th><th>Name</th><th>Ring Offset (s) <span className="text-textSecondary text-xs">— Stratus exclusive (per-member delay)</span></th><th>Ring Duration (s)</th><th>Priority</th><th></th></tr></thead>
        <tbody>
          {(members.data || []).map((m: any) => {
            const ov = (overrides.data || []).find((o: any) => o.extension_uuid === m.extension_uuid) || {};
            return (
              <tr key={m.destination_uuid || m.extension_uuid}>
                <td>{m.user_id || m.extension}</td>
                <td>{m.user_name || m.name}</td>
                <td className="text-xs">{ov.ring_offset_seconds || 0}</td>
                <td className="text-xs">{ov.ring_duration_seconds ?? '—'}</td>
                <td className="text-xs">{ov.priority ?? 0}</td>
                <td><button onClick={() => m.destination_uuid && removeMember.mutate(m.destination_uuid)} disabled={removeMember.isPending} className="btn-ghost p-1 text-danger disabled:opacity-50" title="Remove from ring group"><Trash2 size={14} /></button></td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {!members.data?.length && <div className="p-4 text-center text-textSecondary text-sm">No users yet.</div>}
    </div>
  );
}

function NotificationsTab({ ring_group_uuid, qc, toast }: any) {
  const settings = useQuery<any>({
    queryKey: ['ring-group-settings', ring_group_uuid],
    queryFn: () => fJson(`/api/call-routing/ring-groups/${ring_group_uuid}/settings`),
  });
  const [form, setForm] = useState<any>({});
  const f = { ...(settings.data || {}), ...form };
  const set = (k: string, v: any) => setForm({ ...form, [k]: v });

  const save = useMutation({
    mutationFn: () => fJson(`/api/call-routing/ring-groups/${ring_group_uuid}/settings`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(f),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ring-group-settings', ring_group_uuid] }); toast('success', 'Saved'); },
  });

  return (
    <div className="card p-4 space-y-3">
      <Field label="Voicemail Greeting (audio file)">
        <input className="input font-mono" value={f.vm_greeting_audio_id || ''} onChange={e => set('vm_greeting_audio_id', e.target.value)} placeholder="custom-greeting.wav" />
      </Field>
      <Field label="Voicemail Delivery — Email Options">
        <select className="input" value={f.vm_email_options || 'attach_keep'} onChange={e => set('vm_email_options', e.target.value)}>
          <option value="notify_only">Send email notification only</option>
          <option value="attach_keep">Send voicemail as attachment, keep on server</option>
          <option value="attach_delete">Send voicemail as attachment, delete after</option>
        </select>
      </Field>
      <Field label="Additional Email Recipients (comma-separated)">
        <input className="input" value={f.additional_email_recipients || ''} onChange={e => set('additional_email_recipients', e.target.value)} placeholder="manager@example.com, ops@example.com" />
      </Field>
      <div className="flex justify-end">
        <button onClick={() => save.mutate()} className="btn-primary inline-flex items-center gap-1"><Save size={14} /> Save</button>
      </div>
    </div>
  );
}

function OptionsTab({ ring_group_uuid, qc, toast }: any) {
  const settings = useQuery<any>({
    queryKey: ['ring-group-settings', ring_group_uuid],
    queryFn: () => fJson(`/api/call-routing/ring-groups/${ring_group_uuid}/settings`),
  });
  const [form, setForm] = useState<any>({});
  const f = { ...(settings.data || {}), ...form };
  const set = (k: string, v: any) => setForm({ ...form, [k]: v });

  const save = useMutation({
    mutationFn: () => fJson(`/api/call-routing/ring-groups/${ring_group_uuid}/settings`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(f),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ring-group-settings', ring_group_uuid] }); toast('success', 'Saved'); },
  });

  return (
    <div className="card p-4 space-y-3">
      <Field label="Transcription">
        <select className="input" value={f.transcribe_mode || 'department'} onChange={e => set('transcribe_mode', e.target.value)}>
          <option value="department">Use department settings</option>
          <option value="never">Don't transcribe</option>
          <option value="voicemail_only">Voicemails only</option>
          <option value="always">Always</option>
        </select>
      </Field>
      <label className="text-sm flex items-center gap-2">
        <input type="checkbox" checked={!!f.record_calls} onChange={e => set('record_calls', e.target.checked)} />
        Record all ring group calls
      </label>
      <div className="flex justify-end">
        <button onClick={() => save.mutate()} className="btn-primary inline-flex items-center gap-1"><Save size={14} /> Save</button>
      </div>
    </div>
  );
}

function TalkTab({ ring_group_uuid }: any) {
  return (
    <div className="card p-4">
      <p className="text-sm text-textSecondary">
        Public Talk URL for this ring group is configured at <a href="/talk-config" className="text-primary hover:underline">Talk Config</a>.
      </p>
    </div>
  );
}

function Field({ label, children }: any) {
  return <div><label className="block text-sm text-textSecondary mb-1">{label}</label>{children}</div>;
}
